import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ADMIN_EMAILS } from "./api";
import Timesheet from "./Timesheet";
import Status from "./Status";
import { Holidays, Projects } from "./Admin";
import Download from "./Download";
import switchLogo from "./switch.png";

import "./index.css";

const CheckValues = (value) => {
  if (value == null || ["null", "NULL", "undefined", "UNDEFINED", ""].includes(value)) return "";
  return value;
};

const formatName = (name) =>
  (name || "").toUpperCase().replace(/[^A-Z0-9]+/g, " ").trim().replace(/\s+/g, " ");

// Read when the page mounts (not at import), so a fresh login is picked up without a reload.
const readUserDetails = () => ({
  EmpID: CheckValues(localStorage.getItem("empid")),
  EmpName: CheckValues(formatName(localStorage.getItem("empname"))),
  Location: CheckValues(localStorage.getItem("worklocation")).toUpperCase(),
  Department: CheckValues(localStorage.getItem("dept")).toUpperCase(),
  EmpEmail: CheckValues(localStorage.getItem("emailid")).toLowerCase(),
  ReportingManagerEmpID : CheckValues(localStorage.getItem("reporting_managerempid")),
  ReportingManagerName: CheckValues(formatName(localStorage.getItem("reporting_managername"))),
  ReportingManagerEmail: CheckValues(localStorage.getItem("reporting_manageremail")).toLowerCase(),
});

// userDetails keys the page needs, with the name shown in the alert when one is missing.
const REQUIRED_DETAILS = [
  ["EmpID", "Employee ID"],
  ["EmpName", "Employee name"],
  ["Location", "Location"],
  ["Department", "Department"],
  ["EmpEmail", "Employee email"],
  ["ReportingManagerEmpID", "Reporting manager ID"],
  ["ReportingManagerName", "Reporting manager name"],
  ["ReportingManagerEmail", "Reporting manager email"],
];

const HeaderIcon = ({ children }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);

export default function NewTimeSheet() {
  const navigate = useNavigate();
  const [userDetails] = useState(readUserDetails);
  const missing = REQUIRED_DETAILS.filter(([key]) => !userDetails[key]).map(([, label]) => label);
  const isAdmin = ADMIN_EMAILS.includes(userDetails.EmpEmail);
  const TABS = [
    ["timesheet", "Timesheet"],
    ["status", "Status"],
    ...(isAdmin ? [["holidays", "Holidays"], ["projects", "Projects"], ["download", "Download"]] : []),
  ];

  // Any user detail empty or missing: tell the user once and send them back to the landing page.
  // The ref keeps React's development double-run from showing the alert twice.
  const alerted = useRef(false);
  useEffect(() => {
    if (missing.length === 0 || alerted.current) return;
    alerted.current = true;
    alert(`Your user details are incomplete (missing: ${missing.join(", ")}). Please contact the administrator or log in again.`);
    navigate("/landingPage", { replace: true });
  }, [missing, navigate]);

  const [projects, setProjects] = useState([]);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("timesheet");

  const loadProjects = useCallback(() => {
    api
      .getProjects()
      .then(setProjects)
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => { if (missing.length === 0) loadProjects(); }, [loadProjects, missing.length]);

  // Blue pill that slides under the selected tab: measured from the tab's own position,
  // and re-measured when the tab bar resizes (e.g. the tabs wrap on a phone).
  const navRef = useRef(null);
  const tabRefs = useRef({});
  const [pill, setPill] = useState(null);

  useLayoutEffect(() => {
    const measure = () => {
      const el = tabRefs.current[tab];
      if (el) setPill({ left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight });
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (navRef.current) observer.observe(navRef.current);
    return () => observer.disconnect();
  }, [tab]);

  if (missing.length > 0) return null; // redirecting to the landing page

  return (
    <div className="timesheet-page">
      {/* Full page width so the logo and buttons sit at the edges; content below stays centred. */}
      <header className="timesheet-header">
        <span className="timesheet-logo">
          <img src={switchLogo} alt="Switch" />
        </span>
        <h1 className="timesheet-title">Project Time Tracker</h1>
        <div className="timesheet-header-actions">
          <button className="timesheet-icon-btn" onClick={() => navigate("/landingPage")} title="Home" aria-label="Home">
            <HeaderIcon>
              <path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5" />
            </HeaderIcon>
          </button>
          <button className="timesheet-icon-btn" onClick={() => navigate("/login")} title="Logout" aria-label="Logout">
            <HeaderIcon>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" />
            </HeaderIcon>
          </button>
        </div>
      </header>

      <div className="timesheet-app">
        <nav className="timesheet-tabs" role="tablist" ref={navRef}>
          {pill && <span className="timesheet-tab-pill" style={pill} aria-hidden="true" />}
          {TABS.map(([key, label]) => (
            <button
              key={key}
              ref={(el) => { tabRefs.current[key] = el; }}
              role="tab"
              aria-selected={tab === key}
              className={`timesheet-tab ${tab === key ? "active" : ""}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>

        {error && <div className="timesheet-error">{error}</div>}

        {/* key restarts the enter animation each time the tab changes */}
        <main key={tab} className="timesheet-tab-panel">
          {tab === "timesheet" && <Timesheet userDetails={userDetails} projects={projects} />}
          {tab === "status" && <Status userDetails={userDetails} />}
          {isAdmin && tab === "holidays" && <Holidays userDetails={userDetails} />}
          {isAdmin && tab === "projects" && (
            <Projects userDetails={userDetails} projects={projects} onChange={loadProjects} />
          )}
          {isAdmin && tab === "download" && <Download userDetails={userDetails} />}
        </main>
      </div>
    </div>
  );
}
